import {
	useMutation,
	UseMutationResult,
	useQuery,
	UseQueryResult,
} from '@tanstack/react-query'

import { PaginatedResponse } from '@/core/api/dtos/common/types'
import { EmailAvailabilityRequestDTO } from '@/core/api/dtos/request/users/emailAvailability'
import { UpdateUserRequestDTO } from '@/core/api/dtos/request/users/updateUser'
import { EmailAvailabilityResponseDTO } from '@/core/api/dtos/response/users/emailAvailability'
import { User } from '@/core/api/entities/user'
import { UserLoginToken } from '@/core/api/entities/user/userLoginToken'
import { UserLoginTokenMapper } from '@/core/api/mappers/user/userLoginTokenMapper'
import { UserMapper } from '@/core/api/mappers/userMapper'
import userService from '@/core/api/services/userService'

import { DEFAULT_PARAMS } from './common'

export const useCurrentUser = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<User, Error> => {
	// Use a stable query key - the include params are always the same for current user
	const queryKey = ['currentUser']

	return useQuery({
		queryKey,
		queryFn: () =>
			userService.getCurrentUser(config).then((response) => {
				return UserMapper.toEntity(response.data.data)
			}),
		// Prevent automatic refetching - only refetch when explicitly called
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		// Keep data fresh for a long time
		gcTime: 30 * 60 * 1000, // 30 minutes
		staleTime: 30 * 60 * 1000, // 30 minutes
		...options,
	})
}

export const useUserEmailAvailable = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	EmailAvailabilityResponseDTO,
	Error,
	EmailAvailabilityRequestDTO,
	any
> => {
	return useMutation({
		mutationFn: (data: EmailAvailabilityRequestDTO) =>
			userService.checkEmailAvailability(data, config).then((response) => {
				return response.data.data
			}),
		...options,
	})
}

/**
 * Get users for the account
 */
export const useUsers = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<PaginatedResponse<User>, Error> => {
	const QUERY_KEY = [
		'users',
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			userService.getMyUsers(config).then(({ data: response }) => {
				return {
					data: UserMapper.toEntities(response.data),
					meta: response.meta,
				}
			}),
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
		...options,
	})
}

export const useUpdateUser = ({
	userId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	User,
	Error,
	UpdateUserRequestDTO,
	any
> => {
	return useMutation<User, Error, UpdateUserRequestDTO, any>({
		mutationFn: (data: UpdateUserRequestDTO) => {
			return userService.updateUser(userId, data, config).then((response) => {
				return UserMapper.toEntity(response.data.data)
			})
		},
		...options,
	})
}

export const useDisableUser = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<void, Error, number, any> => {
	return useMutation<void, Error, number, any>({
		mutationFn: (userId: number) => {
			return userService.disableUser(userId, config)
		},
		...options,
	})
}

export const useLoginAsUser = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	UserLoginToken,
	Error,
	number,
	any
> => {
	return useMutation<UserLoginToken, Error, number, any>({
		mutationFn: (userId: number) => {
			return userService.getUserLoginToken(userId, config).then((response) => {
				return UserLoginTokenMapper.toEntity(response.data.data)
			})
		},
		...options,
	})
}
